/**
 * Custom Strata Utilities
 * 
 * Custom strata measures are measures whose stratum identifier ends with a letter.
 * Examples: 118_A, 118_B
 * These measures always use weighted average for aggregation in summary views.
 */

/**
 * Check if a stratum identifier represents a custom strata measure
 * @param stratumId - The stratum identifier (e.g., "118_A", "118_B")
 * @returns True if this is a custom strata measure
 */
export function isCustomStratum(stratumId: string | undefined | null): boolean {
  if (!stratumId || typeof stratumId !== 'string') {
    return false;
  }
  // Check if stratum ends with underscore followed by a letter
  return /^[0-9]+_[A-Z]$/i.test(stratumId);
}

/**
 * Extract the base measure ID from a custom stratum
 * @param stratumId - The stratum identifier (e.g., "118_A")
 * @returns The base measure ID (e.g., "118") or null if not custom
 */
export function getBaseMeasureId(stratumId: string): string | null {
  if (!isCustomStratum(stratumId)) {
    return null;
  }
  // Remove the underscore and letter suffix
  return stratumId.replace(/_[A-Z]$/i, '');
}

/**
 * Check if a measure has custom strata based on its strata list
 * @param strataList - Array of stratum identifiers or strata objects
 * @returns True if any stratum is a custom stratum
 */
export function hasCustomStrata(strataList: Array<string | { name?: string }>): boolean {
  if (!Array.isArray(strataList)) {
    return false;
  }
  return strataList.some(stratum => {
    const stratumId = typeof stratum === 'string' ? stratum : stratum.name;
    return isCustomStratum(stratumId);
  });
}

/**
 * Get the aggregation algorithm for custom strata measures
 * @returns Always returns "Weighted Average" for custom strata
 */
export function getCustomStrataAlgorithm(): string {
  return 'Weighted Average';
}

/**
 * Group custom strata by their base measure ID
 * @param strataList - Array of stratum identifiers
 * @returns Map of base measure ID to array of custom strata
 */
export function groupCustomStrata(strataList: string[]): Record<string, string[]> {
  const groups: Record<string, string[]> = {};
  
  strataList.forEach(stratum => {
    if (isCustomStratum(stratum)) {
      const baseMeasureId = getBaseMeasureId(stratum);
      if (baseMeasureId) {
        if (!groups[baseMeasureId]) {
          groups[baseMeasureId] = [];
        }
        groups[baseMeasureId].push(stratum);
      }
    }
  });
  
  return groups;
}




